import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { PieChartIcon, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HolderDistributionChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/holder-distribution`);
        if (!response.ok) throw new Error('Failed to fetch holder distribution');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <PieChartIcon className="w-5 h-5" />
            Holder Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Holder Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load data</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.distribution?.map(tier => ({
    name: tier.label,
    value: tier.count,
    percentage: tier.percentage_holders,
    supplyPercentage: tier.percentage_supply,
    color: tier.color
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-background/95 border rounded-lg p-3 shadow-lg">
          <p className="font-semibold" style={{ color: item.color }}>{item.name}</p>
          <p className="text-sm text-muted-foreground">
            {item.value.toLocaleString()} holders ({item.percentage}%)
          </p>
          <p className="text-sm text-muted-foreground">
            {item.supplyPercentage}% of supply
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-full" data-testid="holder-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <PieChartIcon className="w-5 h-5" />
          Holder Distribution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center gap-2 mb-2 text-sm text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>{data?.total_holders?.toLocaleString()} total holders</span>
        </div>
        
        <div className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom"
                formatter={(value, entry) => (
                  <span className="text-xs">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="text-center text-xs text-muted-foreground mt-2">
          {data?.circulating_percentage?.toFixed(2)}% supply distributed
        </div>
      </CardContent>
    </Card>
  );
}
